/**
 * A remote's content where it stands: a quiet loading state until it loads,
 * and the remote in its place the moment it does - including when it comes
 * back after being down, which the heartbeat notices. No reload, no Retry.
 *
 * A load that fails because the remote is unreachable waits for its status
 * to turn up and tries again. A load that fails although the remote answered
 * (its expose threw, or the page it built threw) shows the fallback and does
 * not retry on its own: the remote is up, and asking again would only throw
 * again.
 *
 * A remote the platform withholds from the caller (`withheld` answers a
 * reason: killed, not enabled, a flag off) is not loaded at all; the fallback
 * says so, and the remote loads in the moment it is given back.
 *
 * Only the browser loads remotes here. A server render writes the same
 * wrapper and the loading state (or the withheld fallback), with the same
 * structure the browser builds, so the browser adopts it and loads the remote
 * into it.
 */
import { Signal } from '@crown/signal';
import { effect } from '../dom/effect';
import { h } from '../h';
import { hasGlobal } from '../ir/guards';
import { type Instruction, server, sig } from '../ir';
import { loadExpose, RemoteUnavailableError, remoteStatus } from './load';
import { markPluginLoaded } from './plugins';

export interface RemoteProblem {
  readonly remote: string;
  readonly reason: string;
  /** The remote answered, but loading its expose failed. */
  readonly reachable: boolean;
  /** The platform withholds the remote from this caller; `reason` says why. */
  readonly withheld?: boolean;
}

export interface RemoteViewOptions<M> {
  readonly remote: string;
  readonly expose: string;
  readonly build: (module: M) => Instruction;
  readonly fallback: (problem: RemoteProblem) => Instruction;
  /** Shown until the first answer; a quiet busy placeholder when not given. */
  readonly loading?: () => Instruction;
  /**
   * Why the platform withholds the remote from this caller, or null when it
   * does not. Read as a signal: when it turns null the remote loads in.
   */
  readonly withheld?: () => string | null;
  /** Render as a server does - the placeholder, nothing loaded. Defaults to whether there is no document. */
  readonly server?: boolean;
  /** The registry plugin this remote is: once loaded, a security kill of it reloads the page. */
  readonly plugin?: string;
}

type Phase<M> =
  | { readonly kind: 'idle' }
  /** Showing the remote's page as the server rendered it, until the remote has loaded here. */
  | { readonly kind: 'adopting' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'loaded'; readonly module: M }
  | { readonly kind: 'failed'; readonly problem: RemoteProblem };

/** The attribute on the element that holds a remote's content, naming the remote. */
export const REMOTE_ATTRIBUTE = 'data-c-remote';

/** On a remote's loading placeholder: the remote the browser is loading into it. */
export const REMOTE_PENDING_ATTRIBUTE = 'data-c-remote-pending';

/** On a remote's placeholder: the server rendered the remote's page into it. */
export const REMOTE_SSR_ATTRIBUTE = 'data-c-remote-ssr';

/**
 * A remote's page as a server render places it: the remote rendered it (its
 * own renderer, from its own templates), and the browser adopts it as written
 * until the remote has loaded, then takes it over.
 */
export function remoteServerRendered(
  remote: string,
  content: Instruction,
): Instruction {
  return h.div(
    {
      style: 'display: contents',
      [REMOTE_ATTRIBUTE]: remote,
      [REMOTE_SSR_ATTRIBUTE]: '',
    },
    sig(() => server(content)),
  );
}

/** Whether an element holds anything but markers. */
function hasContent(element: Element): boolean {
  for (let node = element.firstChild; node; node = node.nextSibling)
    if (node.nodeType !== 8) return true;
  return false;
}

/** Whether the page holds a server render of `remote` that nothing has adopted yet. */
function serverRendered(remote: string): boolean {
  if (!hasGlobal('document')) return false;
  for (const element of document.querySelectorAll(`[${REMOTE_SSR_ATTRIBUTE}]`))
    if (element.getAttribute(REMOTE_ATTRIBUTE) === remote) return true;
  return false;
}

/** The quiet placeholder a remote's content stands in while it loads: busy, not an error. */
export function remoteLoading(remote: string): Instruction {
  return h.div({
    'aria-busy': 'true',
    class: 'min-h-24',
    [REMOTE_PENDING_ATTRIBUTE]: remote,
  });
}

/**
 * The element a remote's content lives in, as the server writes it: the same
 * wrapper, around the same reactive slot, that `remoteView` builds in the
 * browser, so hydration adopts it instead of building it again.
 */
export function remotePlaceholder(
  remote: string,
  content: Instruction,
): Instruction {
  return h.div(
    { style: 'display: contents', [REMOTE_ATTRIBUTE]: remote },
    sig(() => content),
  );
}

export function remoteView<M>(options: RemoteViewOptions<M>): Instruction {
  const { remote } = options;
  const loadingView = () => options.loading?.() ?? remoteLoading(remote);
  const withheldProblem = (reason: string): RemoteProblem => ({
    remote,
    reason,
    reachable: true,
    withheld: true,
  });

  if (options.server ?? !hasGlobal('document')) {
    const reason = options.withheld?.() ?? null;
    return remotePlaceholder(
      remote,
      reason ? options.fallback(withheldProblem(reason)) : loadingView(),
    );
  }

  // A remote the server rendered stays as written until it has loaded here.
  const adopting = serverRendered(remote);
  const phase = new Signal.State<Phase<M>>(
    adopting ? { kind: 'adopting' } : { kind: 'idle' },
  );
  let alive = true;
  let attempt = 0;

  const load = (): void => {
    const mine = ++attempt;
    if (phase.get().kind !== 'adopting') phase.set({ kind: 'loading' });
    void loadExpose<M>(remote, options.expose).then(
      (module) => {
        if (options.plugin) markPluginLoaded(options.plugin);
        if (alive && mine === attempt) phase.set({ kind: 'loaded', module });
      },
      // A load is refused with an Error - a RemoteUnavailableError from loadExpose - or, from a runtime that throws strings, a message.
      (error: Error | string) => {
        if (!alive || mine !== attempt) return;
        const reachable =
          !(error instanceof RemoteUnavailableError) || error.reachable;
        phase.set({
          kind: 'failed',
          problem: {
            remote,
            reason: error instanceof Error ? error.message : String(error),
            reachable,
          },
        });
      },
    );
  };

  if (!options.withheld?.()) load();

  return h.div(
    {
      style: 'display: contents',
      [REMOTE_ATTRIBUTE]: remote,
      // Mounted: follow the remote's status and whether it is withheld. An
      // unreachable remote that turns up loads again; a remote given back
      // loads for the first time. Disposed: drop whatever lands late.
      ref: (element: Element) => {
        // Claimed: a later view of the same remote is not this server render.
        element.removeAttribute(REMOTE_SSR_ATTRIBUTE);
        // Adopting holds on to the server's HTML only when it was hydrated here. A
        // view mounted afresh (the server's copy did not match, or it is another
        // copy of the remote) has nothing to hold, so it shows it is loading.
        if (phase.get().kind === 'adopting' && !hasContent(element))
          phase.set({ kind: 'loading' });
        const stop = effect(() => {
          const state = remoteStatus(remote).get();
          const withheld = options.withheld?.() ?? null;
          Signal.subtle.untrack(() => {
            if (withheld) return;
            const current = phase.get();
            // Never loaded: withheld when the view was built, and given back now.
            if (
              current.kind === 'idle' ||
              (current.kind === 'adopting' && attempt === 0)
            ) {
              if (current.kind === 'adopting' && !hasContent(element))
                phase.set({ kind: 'loading' });
              load();
            } else if (
              state === 'up' &&
              current.kind === 'failed' &&
              !current.problem.reachable
            )
              load();
          });
        });
        return () => {
          alive = false;
          stop();
        };
      },
    },
    sig(() => {
      const withheld = options.withheld?.() ?? null;
      const current = phase.get();
      return Signal.subtle.untrack(() => {
        if (withheld) return options.fallback(withheldProblem(withheld));
        if (current.kind === 'failed') return options.fallback(current.problem);
        if (current.kind === 'adopting') return server(null);
        if (current.kind !== 'loaded') return loadingView();
        try {
          return options.build(current.module);
        } catch (error) {
          // The remote answered and its page threw: say so, and do not retry.
          const problem: RemoteProblem = {
            remote,
            reason: error instanceof Error ? error.message : String(error),
            reachable: true,
          };
          queueMicrotask(() => {
            if (alive && phase.get() === current)
              phase.set({ kind: 'failed', problem });
          });
          return options.fallback(problem);
        }
      });
    }),
  );
}
